import type { ComponentType, ReactNode } from "react";

import { FormContextProvider } from "../../context/FormContext";
import type { FieldId, FieldValueType, SelfServiceTaskForm } from "../../types";

import { FormContainer } from "./FormContainer";
import { FormProvider } from "./FormProvider";
import { FormRenderer } from "./FormRenderer";

interface FormWrapperProps {
  /** フォーム定義 */
  formDefinition: SelfServiceTaskForm;
  /** 初期値 */
  initialValues?: Record<FieldId, FieldValueType>;
  /** 読み取り専用モード */
  readOnly?: boolean;
  /** テストID */
  "data-testid"?: string;
  /** クラス名 */
  className?: string;
  /** ローディング中に表示するコンポーネント */
  LoadingComponent?: ComponentType;
  /** エラー発生時に表示するコンポーネント */
  ErrorComponent?: ComponentType<{
    error: Error;
    resetError: () => void;
  }>;
  /** 子コンポーネント */
  children?: ReactNode;
  /** オペレーションタスクインスタンスID (HighlyConfidentialTextフィールドのAPI呼び出しに使用) */
  operationTaskInstanceId?: string;
}

/**
 *
 * Form Factoryの主要エントリーポイント
 * - FormProvider: Zustandストアの提供
 * - FormContainer: レンダリング処理
 *
 * 使用例:
 * ```
 * <FormWrapper
 *   formDefinition={formData}
 *   initialValues={initialValues}
 * />
 * ```
 */
export const FormWrapper = ({
  formDefinition,
  initialValues,
  readOnly = false,
  children,
  ErrorComponent: _ErrorComponent,
  operationTaskInstanceId,
  ...containerProps
}: FormWrapperProps) => {
  return (
    <FormProvider>
      <FormContextProvider
        value={{
          operationTaskInstanceId,
        }}
      >
        <FormContainer
          formDefinition={formDefinition}
          initialValues={initialValues}
          {...containerProps}
        >
          {children || (
            <FormRenderer
              formDefinition={formDefinition}
              readOnly={readOnly}
              {...containerProps}
            />
          )}
        </FormContainer>
      </FormContextProvider>
    </FormProvider>
  );
};

FormWrapper.displayName = "FormWrapper";
